import { useMemo, useState } from "react";
import { RotateCcw, Search, X } from "lucide-react";
import {
  createPosReturn,
  getSaleForReturn,
} from "../../../services/pos";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function PosReturnModal({
  open,
  onClose,
  accessToken,
  onSuccess,
}) {
  const [receiptNumber, setReceiptNumber] = useState("");
  const [sale, setSale] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [reason, setReason] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [paymentReference, setPaymentReference] = useState("");
  const [loadingSale, setLoadingSale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setReceiptNumber("");
    setSale(null);
    setQuantities({});
    setReason("");
    setPaymentMethod("CASH");
    setPaymentReference("");
    setError("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const findReceipt = async () => {
    if (!receiptNumber.trim()) {
      setError("Enter a receipt number");
      return;
    }

    setLoadingSale(true);
    setError("");

    try {
      const response = await getSaleForReturn(receiptNumber, accessToken);
      setSale(response);
      setQuantities({});
    } catch (requestError) {
      setSale(null);
      setError(requestError.message || "Receipt was not found");
    } finally {
      setLoadingSale(false);
    }
  };

  const updateQuantity = (saleItemId, value, maximumQuantity) => {
    const requested = Math.max(0, Number(value) || 0);
    const capped = Math.min(requested, Number(maximumQuantity || 0));

    setQuantities((previous) => ({
      ...previous,
      [saleItemId]: capped,
    }));
  };

  const selectedItems = useMemo(() => {
    if (!sale?.items) return [];

    return sale.items
      .map((item) => {
        const quantity = Number(quantities[item.id] || 0);

        if (quantity <= 0) return null;

        const soldQuantity = Number(item.quantitySold || 0);
        const lineTotal = Number(item.lineTotalAmount || 0);

        return {
          ...item,
          quantity,
          estimatedRefund:
            soldQuantity > 0 ? (lineTotal * quantity) / soldQuantity : 0,
        };
      })
      .filter(Boolean);
  }, [quantities, sale]);

  const estimatedRefund = useMemo(
    () =>
      selectedItems.reduce(
        (total, item) => total + item.estimatedRefund,
        0,
      ),
    [selectedItems],
  );

  const submitReturn = async () => {
    if (!sale) {
      setError("Find a completed receipt first");
      return;
    }

    if (!selectedItems.length) {
      setError("Select at least one item to return");
      return;
    }

    if (!reason.trim()) {
      setError("Enter a return reason");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const createdReturn = await createPosReturn(
        {
          receiptNumber: sale.receiptNumber,
          reason: reason.trim(),
          items: selectedItems.map((item) => ({
            saleItemId: item.id,
            quantity: item.quantity,
          })),
          refundPayments: [
            {
              method: paymentMethod,
              amount: Number(estimatedRefund.toFixed(2)),
              reference: paymentReference.trim() || undefined,
            },
          ],
        },
        accessToken,
      );

      await onSuccess(createdReturn);
      close();
    } catch (requestError) {
      setError(requestError.message || "Unable to complete return");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
      <div className="mx-auto my-6 w-full max-w-5xl rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary-50 text-primary-800">
              <RotateCcw size={21} />
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                POS Return
              </h2>
              <p className="text-sm text-gray-500">
                Return physically received items against an original receipt.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={close}
            className="grid h-10 w-10 place-items-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-7">
          {error && (
            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search
                size={19}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                value={receiptNumber}
                onChange={(event) =>
                  setReceiptNumber(event.target.value.toUpperCase())
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") findReceipt();
                }}
                placeholder="Enter receipt number, e.g. POS-000001"
                className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 font-semibold outline-none focus:border-primary-800"
              />
            </div>

            <button
              type="button"
              disabled={loadingSale}
              onClick={findReceipt}
              className="rounded-xl bg-primary-800 px-6 font-semibold text-white disabled:opacity-50"
            >
              {loadingSale ? "Finding…" : "Find Receipt"}
            </button>
          </div>

          {sale && (
            <>
              <div className="mt-6 grid gap-4 rounded-2xl bg-[#F8F6F5] p-5 md:grid-cols-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Receipt
                  </p>
                  <p className="mt-1 font-bold text-primary-800">
                    {sale.receiptNumber}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Customer
                  </p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {sale.customer?.fullName || "Walk-in Customer"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Original Bill
                  </p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {money(sale.totalAmount)}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Sale Date
                  </p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {new Date(sale.completedAt).toLocaleDateString("en-IN")}
                  </p>
                </div>
              </div>

              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[850px] text-sm">
                    <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                      <tr>
                        <th className="px-4 py-3">Item</th>
                        <th className="px-4 py-3">SKU</th>
                        <th className="px-4 py-3 text-center">Sold</th>
                        <th className="px-4 py-3 text-center">Previously Returned</th>
                        <th className="px-4 py-3 text-center">Returnable</th>
                        <th className="px-4 py-3 text-center">Return Qty</th>
                        <th className="px-4 py-3 text-right">Estimated Refund</th>
                      </tr>
                    </thead>

                    <tbody>
                      {sale.items.map((item) => {
                        const returnable = Number(item.quantityReturnable || 0);
                        const quantity = quantities[item.id] || "";

                        return (
                          <tr
                            key={item.id}
                            className="border-t border-slate-100"
                          >
                            <td className="px-4 py-4">
                              <p className="font-semibold text-gray-900">
                                {item.productName}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                {item.variantName || "Default Variant"}
                              </p>
                            </td>

                            <td className="px-4 py-4 font-mono text-xs font-semibold text-primary-800">
                              {item.sku}
                            </td>

                            <td className="px-4 py-4 text-center">
                              {Number(item.quantitySold)}
                            </td>

                            <td className="px-4 py-4 text-center text-gray-500">
                              {Number(item.quantityAlreadyReturned)}
                            </td>

                            <td className="px-4 py-4 text-center font-bold text-emerald-700">
                              {returnable}
                            </td>

                            <td className="px-4 py-4 text-center">
                              <input
                                type="number"
                                min="0"
                                max={returnable}
                                step="0.001"
                                disabled={returnable <= 0}
                                value={quantity}
                                onChange={(event) =>
                                  updateQuantity(
                                    item.id,
                                    event.target.value,
                                    returnable,
                                  )
                                }
                                className="h-9 w-24 rounded-lg border border-gray-300 text-center font-semibold outline-none focus:border-primary-800 disabled:bg-gray-100"
                              />
                            </td>

                            <td className="px-4 py-4 text-right font-bold text-primary-800">
                              {money(
                                Number(item.lineTotalAmount || 0) *
                                  ((Number(quantity) || 0) /
                                    Number(item.quantitySold || 1)),
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6 grid gap-5 lg:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Return reason *
                  </label>

                  <textarea
                    rows={4}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Damaged, wrong size, customer return, etc."
                    className="mt-2 w-full rounded-2xl border border-gray-200 p-4 outline-none focus:border-primary-800"
                  />
                </div>

                <div className="rounded-2xl bg-[#F8F6F5] p-5">
                  <p className="text-sm font-semibold text-gray-900">
                    Refund Details
                  </p>

                  <div className="mt-4 flex gap-3">
                    <select
                      value={paymentMethod}
                      onChange={(event) => setPaymentMethod(event.target.value)}
                      className="h-11 flex-1 rounded-xl border border-gray-200 bg-white px-3 outline-none focus:border-primary-800"
                    >
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="CARD">Card</option>
                      <option value="BANK_TRANSFER">Bank Transfer</option>
                      <option value="STORE_CREDIT">Store Credit</option>
                      <option value="OTHER">Other</option>
                    </select>

                    <input
                      value={paymentReference}
                      onChange={(event) =>
                        setPaymentReference(event.target.value)
                      }
                      placeholder="Reference (optional)"
                      className="h-11 flex-1 rounded-xl border border-gray-200 bg-white px-3 outline-none focus:border-primary-800"
                    />
                  </div>

                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                    <span className="font-semibold text-gray-700">
                      Estimated refund
                    </span>
                    <span className="text-2xl font-bold text-primary-800">
                      {money(estimatedRefund)}
                    </span>
                  </div>

                  <p className="mt-3 text-xs text-gray-500">
                    The server rechecks quantities and calculates the final
                    refund before saving.
                  </p>
                </div>
              </div>

              <div className="mt-7 flex justify-end gap-3 border-t border-gray-100 pt-6">
                <button
                  type="button"
                  onClick={close}
                  className="rounded-xl border border-gray-200 px-6 py-3 font-semibold text-gray-700"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={saving || !selectedItems.length}
                  onClick={submitReturn}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-6 py-3 font-semibold text-white disabled:opacity-50"
                >
                  <RotateCcw size={17} />
                  {saving ? "Processing…" : `Complete Return · ${money(estimatedRefund)}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}